import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import ModalMC from "../../../components/ModalMC";
import FormProviderMC from "../../../components/FormProviderMC";
import DropdownMC from "../../../components/DropdownMC";
import InputComponent from "../../../components/InputMC";
import MessageCounterMC from "./MessageCounterMC";
import DropzoneFileUploader from "../../../components/DropzoneFileUploader";
import InputComponentConditionalMC from "../../../components/InputComponentConditionalMC";
import { usePaymentTransactionForm } from "../../../hooks/inbox/forms/usePaymentTransactionForm";
import { usePaymentTransactionActions } from "../../../hooks/inbox/actions/usePaymentTransactionActions";
import { usePaymentTransactionUI } from "../../../hooks/inbox/ui/usePaymentTransactionUI";
import { useTraceUpdate } from "../../../hooks/shared/useDebugRenders";

/**
 * PaymentTransactionModalMC - Componente para mostrar el modal de transacción de pago en el Inbox.
 */
const PaymentTransactionModalMC = memo(({ order, newStatus, updateOrderInList, toggleModal }) => {
  const { t } = useTranslation('InboxMC');
  // Separación de lógica: hooks especializados
  const {
    formMethods,
    paymentOptions,
    maxChars
  } = usePaymentTransactionForm();

  const {
    loading,
    handleSaveButtonClick
  } = usePaymentTransactionActions({
    order,
    newStatus,
    updateOrderInList,
    toggleModal,
    formMethods
  });

  const { modalTitle, containerClassName, modalClassName, headerClassName, footerClassName, getButtons } = usePaymentTransactionUI();

  // Debug tracking de dependencias críticas
  useTraceUpdate('PaymentTransactionModalMC', {
    orderId: order?.id,
    newStatus,
    loading
  }, {
    logRenderCount: true,
    logPropChanges: true
  });

  const buttons = getButtons({ loading, handleSaveButtonClick });

  return (
    <ModalMC
      visible
      onClose={toggleModal}
      title={modalTitle}
      containerClassName={containerClassName}
      className={modalClassName}
      headerClassName={headerClassName}
      footerClassName={footerClassName}
      buttons={buttons}
    >
      <div className="tw-flex-1 tw-overflow-auto tw-p-4">
        <FormProviderMC methods={formMethods}>
          <div className="tw-p-4">
            <DropdownMC
              useFloatingLabel
              options={paymentOptions}
              label={t('orders.payment_method')}
              name="paymentMethod"
              className="tw-mb-3"
              placeholder={t('orders.select_payment_method')}
            />
            <InputComponentConditionalMC
              formMethods={formMethods}
              fieldName="paymentMethod"
              showWhenValue="other"
              inputName="paymentOther"
              label={t('orders.other_payment_method')}
              placeholder={t('orders.indicate_payment_method')}
              className="tw-flex-1 tw-min-w-0 tw-resize-none focus:tw-ring-0 focus:tw-shadow-none focus:tw-outline-none focus:tw-ring-transparent focus:tw-border-gray-200 tw-mb-3"
            />
            <DropzoneFileUploader
              name="attachment"
              accept=".png,.jpg,.jpeg,.pdf"
              placeholder={t('orders.upload_payment_attachment')}
              className="tw-mb-3"
              fileContainerClassName="tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-xl tw-px-3"
              defaultValue={formMethods.watch('attachment')}
            />
            <div className="tw-border tw-border-gray-200 tw-rounded-xl tw-p-1 tw-flex tw-flex-col tw-gap-1 tw-items-stretch tw-w-full tw-flex-1 tw-min-w-0 tw-bg-gray-50">
              <div className="tw-flex tw-w-full tw-items-center tw-gap-2">
                <InputComponent
                  textarea
                  useFloatingLabel
                  hideBorder
                  label={t('orders.note')}
                  name="note"
                  maxLength={maxChars}
                  containerClass="tw-flex-1 tw-min-w-0"
                  floatingLabelClassName="tw-start-3"
                  className="tw-bg-transparent tw-flex-1 tw-min-w-0 tw-resize-none focus:tw-ring-0 focus:tw-border-transparent focus:tw-shadow-none focus:tw-outline-none focus:tw-ring-transparent focus:tw-border-0 !tw-px-3"
                />
              </div>
              <MessageCounterMC name="note" methods={formMethods} maxChars={maxChars} />
            </div>
          </div>
        </FormProviderMC>
      </div>
    </ModalMC>
  );
});

PaymentTransactionModalMC.displayName = 'PaymentTransactionModalMC';

export default PaymentTransactionModalMC;